import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Settings2, Plus, Trash2, Edit, GripVertical, Eye, EyeOff, Copy,
  ChevronDown, ChevronRight, Save, RefreshCw, Layers, FileText,
  ToggleLeft, Type, Hash, Calendar, List, CheckSquare, Info, AlertCircle,
  MessageSquare, ClipboardList, Footprints
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../components/ui/tabs";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "../components/ui/tooltip";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const FORM_TYPES = [
  { value: "lead_capture", label: "Lead Capture Form", icon: FileText },
  { value: "lead_internal", label: "Internal Lead Form", icon: FileText },
  { value: "eoi", label: "EOI Registration (CP)", icon: FileText },
  { value: "eoi_internal", label: "EOI Creation (Internal)", icon: FileText },
  { value: "site_visit", label: "Site Visit Form", icon: Calendar },
  { value: "revisit", label: "Revisit Form", icon: RefreshCw },
  { value: "interaction", label: "Interaction Form", icon: MessageSquare },
  { value: "post_visit_analysis", label: "Post-Visit Analysis", icon: ClipboardList },
  { value: "walk_in", label: "Walk-In Entry", icon: Footprints },
];

const FIELD_TYPES = [
  { value: "text", label: "Text", icon: Type },
  { value: "textarea", label: "Text Area", icon: Type },
  { value: "number", label: "Number", icon: Hash },
  { value: "phone", label: "Phone", icon: Type },
  { value: "email", label: "Email", icon: Type },
  { value: "dropdown", label: "Dropdown", icon: List },
  { value: "multi_select", label: "Multi Select", icon: CheckSquare },
  { value: "date", label: "Date", icon: Calendar },
  { value: "boolean", label: "Yes/No Toggle", icon: ToggleLeft },
  { value: "radio", label: "Radio Buttons", icon: List },
  { value: "hidden", label: "Hidden", icon: EyeOff },
];

const FIELD_GROUPS = [
  { value: "contact", label: "Contact Information" },
  { value: "preferences", label: "Preferences" },
  { value: "profile", label: "Buyer Profile" },
  { value: "timeline", label: "Timeline" },
  { value: "utm", label: "UTM Tracking" },
  { value: "other", label: "Other" },
];

const FormBuilderPage = () => {
  const [configs, setConfigs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Current config being edited
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editedFields, setEditedFields] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  // Create form state
  const [newConfig, setNewConfig] = useState({
    form_type: "lead_capture",
    name: "",
    description: "",
    project_id: null,
    source: null,
  });
  
  // Drag state
  const [draggedField, setDraggedField] = useState(null);

  useEffect(() => {
    fetchConfigs();
    fetchProjects();
  }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API}/form-config`);
      setConfigs(res.data);
    } catch (e) {
      toast.error("Failed to load form configurations");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      console.error("Failed to load projects");
    }
  };

  const selectConfig = async (config) => {
    if (hasChanges) {
      if (!window.confirm("You have unsaved changes. Discard them?")) {
        return;
      }
    }
    setSelectedConfig(config);
    setEditedFields([...config.fields].sort((a, b) => a.order - b.order));
    setHasChanges(false);
  };

  const handleCreateConfig = async () => {
    try {
      // Get default fields for the form type
      const defaultsRes = await axios.get(`${API}/form-config/defaults/${newConfig.form_type}`);
      
      const res = await axios.post(`${API}/form-config`, {
        ...newConfig,
        fields: defaultsRes.data.fields,
      });
      
      toast.success("Form configuration created");
      setShowCreateDialog(false);
      setNewConfig({ form_type: "lead_capture", name: "", description: "", project_id: null, source: null });
      fetchConfigs();
      
      // Select the new config
      const newConfigData = await axios.get(`${API}/form-config/${res.data.config_id}`);
      selectConfig(newConfigData.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create configuration");
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedConfig) return;
    setSaving(true);
    try {
      await axios.put(`${API}/form-config/${selectedConfig.config_id}`, {
        fields: editedFields,
      });
      toast.success("Configuration saved");
      setHasChanges(false);
      fetchConfigs();
    } catch (e) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateConfig = async (configId) => {
    try {
      const res = await axios.post(`${API}/form-config/${configId}/duplicate`);
      toast.success("Configuration duplicated");
      fetchConfigs();
    } catch (e) {
      toast.error("Failed to duplicate configuration");
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm("Are you sure you want to delete this configuration?")) return;
    try {
      await axios.delete(`${API}/form-config/${configId}`);
      toast.success("Configuration deleted");
      if (selectedConfig?.config_id === configId) {
        setSelectedConfig(null);
        setEditedFields([]);
      }
      fetchConfigs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete configuration");
    }
  };

  const handleFieldChange = (fieldId, key, value) => {
    setEditedFields(prev => prev.map(f => 
      f.field_id === fieldId ? { ...f, [key]: value } : f
    ));
    setHasChanges(true);
  };

  const handleToggleRequired = (fieldId) => {
    setEditedFields(prev => prev.map(f => 
      f.field_id === fieldId ? { ...f, required: !f.required } : f
    ));
    setHasChanges(true);
  };

  const handleToggleVisible = (fieldId) => {
    setEditedFields(prev => prev.map(f => 
      f.field_id === fieldId ? { ...f, visible: !f.visible } : f
    ));
    setHasChanges(true);
  };

  const handleAddField = () => {
    setEditingField({
      field_id: `field_${Date.now()}`,
      name: "",
      label: "",
      field_type: "text",
      placeholder: "",
      help_text: "",
      required: false,
      visible: true,
      order: editedFields.length + 1,
      options: [],
      is_custom_field: true,
      is_system_field: false,
      group: "other",
    });
    setShowFieldDialog(true);
  };

  const handleEditField = (field) => {
    setEditingField({ ...field });
    setShowFieldDialog(true);
  };

  const handleSaveField = () => {
    if (!editingField.name || !editingField.label) {
      toast.error("Field name and label are required");
      return;
    }
    
    const existingIndex = editedFields.findIndex(f => f.field_id === editingField.field_id);
    if (existingIndex >= 0) {
      setEditedFields(prev => prev.map(f => 
        f.field_id === editingField.field_id ? editingField : f
      ));
    } else {
      setEditedFields(prev => [...prev, editingField]);
    }
    
    setHasChanges(true);
    setShowFieldDialog(false);
    setEditingField(null);
  };

  const handleRemoveField = (field) => {
    if (field.is_system_field) {
      // Just hide system fields
      handleToggleVisible(field.field_id);
      toast.info("System field hidden (cannot be deleted)");
    } else {
      if (!window.confirm(`Remove field "${field.label}"?`)) return;
      setEditedFields(prev => prev.filter(f => f.field_id !== field.field_id));
      setHasChanges(true);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, field) => {
    setDraggedField(field);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, targetField) => {
    e.preventDefault();
    if (!draggedField || draggedField.field_id === targetField.field_id) return;
    
    const draggedIdx = editedFields.findIndex(f => f.field_id === draggedField.field_id);
    const targetIdx = editedFields.findIndex(f => f.field_id === targetField.field_id);
    
    if (draggedIdx === targetIdx) return;
    
    const newFields = [...editedFields];
    newFields.splice(draggedIdx, 1);
    newFields.splice(targetIdx, 0, draggedField);
    
    // Update order numbers
    newFields.forEach((f, i) => { f.order = i + 1; });
    
    setEditedFields(newFields);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
  };

  // Group fields for display
  const groupedFields = editedFields.reduce((acc, field) => {
    const group = field.group || "other";
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {});

  const getFieldTypeIcon = (type) => {
    const ft = FIELD_TYPES.find(t => t.value === type);
    return ft ? ft.icon : Type;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="form-builder-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Form Builder</h1>
            <p className="text-sm text-slate-500 mt-1">
              Customize lead capture, EOI, and site visit forms
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-config-btn">
            <Plus className="w-4 h-4 mr-2" /> New Configuration
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Config List Sidebar */}
          <div className="col-span-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-700">
                  Form Configurations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                {configs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No configurations yet. Create one to get started.
                  </p>
                ) : (
                  configs.map(config => (
                    <div
                      key={config.config_id}
                      onClick={() => selectConfig(config)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedConfig?.config_id === config.config_id
                          ? "bg-blue-50 border-blue-300"
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`config-item-${config.config_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {config.name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">
                              {config.form_type}
                            </Badge>
                            {config.is_default && (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">
                                Default
                              </Badge>
                            )}
                            {!config.is_active && (
                              <Badge className="bg-slate-100 text-slate-500 text-[10px]">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          {config.project_id && (
                            <p className="text-[10px] text-slate-400 mt-1">
                              Project: {projects.find(p => p.project_id === config.project_id)?.name || config.project_id}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => { e.stopPropagation(); handleDuplicateConfig(config.config_id); }}
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Duplicate</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {!config.is_default && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteConfig(config.config_id); }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Field Editor */}
          <div className="col-span-8">
            {selectedConfig ? (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-900">
                      {selectedConfig.name}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {editedFields.length} fields • Drag to reorder
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddField}
                      data-testid="add-field-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Field
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveConfig}
                      disabled={!hasChanges || saving}
                      data-testid="save-config-btn"
                    >
                      {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      {hasChanges ? "Save Changes" : "Saved"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 max-h-[calc(100vh-320px)] overflow-y-auto">
                  {Object.entries(groupedFields).map(([group, fields]) => (
                    <div key={group} className="space-y-2">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                        <Layers className="w-3 h-3" />
                        {FIELD_GROUPS.find(g => g.value === group)?.label || group}
                      </h4>
                      <div className="space-y-1.5">
                        {fields.sort((a, b) => a.order - b.order).map(field => {
                          const FieldIcon = getFieldTypeIcon(field.field_type);
                          return (
                            <div
                              key={field.field_id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, field)}
                              onDragOver={(e) => handleDragOver(e, field)}
                              onDragEnd={handleDragEnd}
                              className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                                draggedField?.field_id === field.field_id
                                  ? "opacity-50 bg-slate-100"
                                  : field.visible
                                  ? "bg-white border-slate-200 hover:border-slate-300"
                                  : "bg-slate-50 border-slate-200 opacity-60"
                              }`}
                              data-testid={`field-row-${field.field_id}`}
                            >
                              <GripVertical className="w-4 h-4 text-slate-400 cursor-grab shrink-0" />
                              
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FieldIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-800 truncate">
                                    {field.label}
                                  </p>
                                  <p className="text-[10px] text-slate-400 truncate">
                                    {field.name} • {field.field_type}
                                    {field.is_system_field && " • System"}
                                    {field.is_custom_field && " • Custom"}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 shrink-0">
                                {/* Required toggle */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => handleToggleRequired(field.field_id)}
                                        className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                                          field.required
                                            ? "bg-red-100 text-red-700"
                                            : "bg-slate-100 text-slate-500"
                                        }`}
                                      >
                                        {field.required ? "Required" : "Optional"}
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Toggle required</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                
                                {/* Visibility toggle */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={() => handleToggleVisible(field.field_id)}
                                      >
                                        {field.visible ? (
                                          <Eye className="w-3.5 h-3.5 text-slate-600" />
                                        ) : (
                                          <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>{field.visible ? "Hide" : "Show"}</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                
                                {/* Edit button */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={() => handleEditField(field)}
                                      >
                                        <Edit className="w-3.5 h-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit field</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                
                                {/* Delete button */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                                        onClick={() => handleRemoveField(field)}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {field.is_system_field ? "Hide field" : "Remove field"}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  
                  {editedFields.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Settings2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No fields yet. Click "Add Field" to start.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center min-h-[400px]">
                <div className="text-center text-slate-500">
                  <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Select a configuration to edit</p>
                  <p className="text-sm mt-1">Or create a new one to get started</p>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Create Config Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Form Configuration</DialogTitle>
              <DialogDescription>
                Create a new form configuration for a specific form type, project, or source.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Configuration Name *</Label>
                <Input
                  placeholder="e.g., Skyline Heights Lead Form"
                  value={newConfig.name}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Form Type *</Label>
                <Select
                  value={newConfig.form_type}
                  onValueChange={(v) => setNewConfig(prev => ({ ...prev, form_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORM_TYPES.map(ft => (
                      <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Project (Optional)</Label>
                <Select
                  value={newConfig.project_id || "none"}
                  onValueChange={(v) => setNewConfig(prev => ({ ...prev, project_id: v === "none" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All Projects (Global)</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Leave empty for a global configuration
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Source (Optional)</Label>
                <Select
                  value={newConfig.source || "none"}
                  onValueChange={(v) => setNewConfig(prev => ({ ...prev, source: v === "none" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All Sources</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="cp_portal">Channel Partner Portal</SelectItem>
                    <SelectItem value="walk_in">Walk-in</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Optional description..."
                  value={newConfig.description}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateConfig}
                disabled={!newConfig.name || !newConfig.form_type}
              >
                Create Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Field Dialog */}
        <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingField?.is_custom_field ? "Edit Custom Field" : "Edit Field"}
              </DialogTitle>
            </DialogHeader>
            {editingField && (
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Field Name *</Label>
                    <Input
                      placeholder="e.g., alternate_phone"
                      value={editingField.name}
                      onChange={(e) => setEditingField(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                      disabled={editingField.is_system_field}
                    />
                    <p className="text-xs text-slate-500">Internal identifier (no spaces)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Display Label *</Label>
                    <Input
                      placeholder="e.g., Alternate Phone"
                      value={editingField.label}
                      onChange={(e) => setEditingField(prev => ({ ...prev, label: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Field Type</Label>
                    <Select
                      value={editingField.field_type}
                      onValueChange={(v) => setEditingField(prev => ({ ...prev, field_type: v }))}
                      disabled={editingField.is_system_field}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(ft => (
                          <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Group</Label>
                    <Select
                      value={editingField.group || "other"}
                      onValueChange={(v) => setEditingField(prev => ({ ...prev, group: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_GROUPS.map(g => (
                          <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Placeholder</Label>
                  <Input
                    placeholder="Placeholder text..."
                    value={editingField.placeholder || ""}
                    onChange={(e) => setEditingField(prev => ({ ...prev, placeholder: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Help Text</Label>
                  <Input
                    placeholder="Additional instructions for users..."
                    value={editingField.help_text || ""}
                    onChange={(e) => setEditingField(prev => ({ ...prev, help_text: e.target.value }))}
                  />
                </div>
                
                {/* Options for dropdown/radio/multi-select */}
                {["dropdown", "radio", "multi_select"].includes(editingField.field_type) && (
                  <div className="space-y-2">
                    <Label>Options (one per line, format: value|label)</Label>
                    <Textarea
                      placeholder="professional|Professional&#10;business_owner|Business Owner&#10;other|Other"
                      value={(editingField.options || []).map(o => `${o.value}|${o.label}${o.triggers_custom ? '|custom' : ''}`).join('\n')}
                      onChange={(e) => {
                        const options = e.target.value.split('\n').filter(l => l.trim()).map(line => {
                          const parts = line.split('|');
                          return {
                            value: parts[0]?.trim() || "",
                            label: parts[1]?.trim() || parts[0]?.trim() || "",
                            triggers_custom: parts[2]?.trim() === 'custom',
                          };
                        });
                        setEditingField(prev => ({ ...prev, options }));
                      }}
                      rows={4}
                    />
                    <p className="text-xs text-slate-500">
                      Add |custom at end to show custom input (e.g., other|Other|custom)
                    </p>
                  </div>
                )}
                
                {/* Conditional logic */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Conditional Display
                    <Badge variant="outline" className="text-[10px]">Optional</Badge>
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      value={editingField.conditional?.depends_on || "none"}
                      onValueChange={(v) => {
                        if (v === "none") {
                          setEditingField(prev => ({ ...prev, conditional: null }));
                        } else {
                          setEditingField(prev => ({
                            ...prev,
                            conditional: { depends_on: v, operator: "equals", value: "" }
                          }));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Depends on..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No condition</SelectItem>
                        {editedFields.filter(f => f.field_id !== editingField.field_id).map(f => (
                          <SelectItem key={f.field_id} value={f.field_id}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editingField.conditional && (
                      <>
                        <Select
                          value={editingField.conditional.operator || "equals"}
                          onValueChange={(v) => setEditingField(prev => ({
                            ...prev,
                            conditional: { ...prev.conditional, operator: v }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">Equals</SelectItem>
                            <SelectItem value="not_equals">Not Equals</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Value..."
                          value={editingField.conditional.value || ""}
                          onChange={(e) => setEditingField(prev => ({
                            ...prev,
                            conditional: { ...prev.conditional, value: e.target.value }
                          }))}
                        />
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingField.required}
                      onCheckedChange={(v) => setEditingField(prev => ({ ...prev, required: v }))}
                    />
                    <Label className="text-sm">Required field</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingField.visible}
                      onCheckedChange={(v) => setEditingField(prev => ({ ...prev, visible: v }))}
                    />
                    <Label className="text-sm">Visible on form</Label>
                  </div>
                </div>
                
                {editingField.is_system_field && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      This is a system field. Some properties cannot be changed.
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFieldDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveField}>
                Save Field
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FormBuilderPage;
